import { CommonModule } from '@angular/common';
import { Component } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTableModule } from '@angular/material/table';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ImportarLancamentoService } from '../../../../../shared/services/apis/financas/importar-lancamento.service';
import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { ItemValidacaoMovimentacaoStruct } from '../../../../../shared/services/structs/financas/item-validacao-movimentacao.struct';
import { ValidacaoImportacaoMovimentacaoResponse } from '../../../../../shared/services/responses/financas/validacao-importacao-movimentacao.response';

type FilterType = 'todos' | 'novos' | 'avisos' | 'erros';

@Component({
    selector: 'app-modal-importar-lancamentos',
    standalone: true,
    imports: [
        CommonModule,
        MatDialogModule,
        MatButtonModule,
        MatIconModule,
        MatProgressSpinnerModule,
        MatTableModule,
        MatTooltipModule,
    ],
    templateUrl: './modal-importar-lancamentos.component.html',
    styleUrls: ['./modal-importar-lancamentos.component.css']
})
export class ModalImportarLancamentosComponent {
    public readonly colunas = ['Data', 'Categoria', 'Histórico', 'Tipo', 'Valor', 'Tags', 'Conta'];
    public readonly displayedColumns = ['status', 'data', 'tipo', 'valor', 'conta', 'categoria', 'historico', 'tags'];

    public step = 1;
    public selectedFile: File | null = null;
    public isDragOver = false;
    public isLoading = false;

    public validationResponse: ValidacaoImportacaoMovimentacaoResponse | null = null;
    public itemsFiltered: ItemValidacaoMovimentacaoStruct[] = [];
    public currentFilter: FilterType = 'todos';

    constructor(
        public dialogRef: MatDialogRef<ModalImportarLancamentosComponent>,
        private importarLancamentoService: ImportarLancamentoService,
        private alertService: AlertService,
    ) { }

    // ── Step 1 handlers ──────────────────────────────────────────

    public onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = true;
    }

    public onDrop(event: DragEvent): void {
        event.preventDefault();
        this.isDragOver = false;
        const file = event.dataTransfer?.files[0];
        if (file) this.processFile(file);
    }

    public onFileSelected(event: Event): void {
        const input = event.target as HTMLInputElement;
        if (input.files?.[0]) this.processFile(input.files[0]);
    }

    public removerArquivo(event: Event): void {
        event.stopPropagation();
        this.selectedFile = null;
    }

    public downloadModelo(): void {
        const link = document.createElement('a');
        link.href = 'assets/files/exemplo_planilha_lancamentos.csv';
        link.download = 'exemplo_planilha_lancamentos.csv';
        link.click();
    }

    private processFile(file: File): void {
        this.selectedFile = file;
        this.isLoading = true;

        const reader = new FileReader();
        reader.readAsDataURL(file);
        reader.onload = () => {
            const base64 = (reader.result as string).split(',')[1];
            this.importarLancamentoService.validar(base64, file.name).subscribe({
                next: (response) => {
                    this.isLoading = false;
                    if (response.isError) {
                        this.alertService.show('Erro na validação', response.errorDescription, AlertType.error);
                        this.selectedFile = null;
                    } else {
                        this.validationResponse = response;
                        this.step = 2;
                        this.aplicarFiltro('todos');
                    }
                },
                error: () => {
                    this.isLoading = false;
                    this.selectedFile = null;
                    this.alertService.show('Erro', 'Falha ao processar arquivo.', AlertType.error);
                }
            });
        };
        reader.onerror = () => {
            this.isLoading = false;
            this.selectedFile = null;
            this.alertService.show('Erro', 'Erro ao ler arquivo.', AlertType.error);
        };
    }

    // ── Step 2 handlers ──────────────────────────────────────────

    public voltarParaUpload(): void {
        this.step = 1;
        this.selectedFile = null;
        this.validationResponse = null;
        this.itemsFiltered = [];
    }

    public aplicarFiltro(tipo: FilterType): void {
        this.currentFilter = tipo;
        if (!this.validationResponse) return;
        const itens = this.validationResponse.itens;
        switch (tipo) {
            case 'todos':   this.itemsFiltered = itens; break;
            case 'novos':   this.itemsFiltered = itens.filter(i => i.isValido); break;
            case 'avisos':  this.itemsFiltered = itens.filter(i => i.isAlerta && i.isValido); break;
            case 'erros':   this.itemsFiltered = itens.filter(i => !i.isValido); break;
        }
    }

    public confirmarImportacao(): void {
        if (this.isImportDisabled || this.isLoading) return;

        const itensValidos = this.validationResponse!.itens.filter(i => i.isValido);
        this.isLoading = true;

        this.importarLancamentoService.confirmar(itensValidos).subscribe({
            next: (response) => {
                this.isLoading = false;
                if (response.isError) {
                    this.alertService.show('Erro', response.errorDescription, AlertType.error);
                } else {
                    this.alertService.show('Sucesso', 'Lançamentos importados com sucesso.', AlertType.success);
                    this.dialogRef.close(true);
                }
            },
            error: () => {
                this.isLoading = false;
                this.alertService.show('Erro', 'Falha ao confirmar importação.', AlertType.error);
            }
        });
    }

    // ── Helpers ──────────────────────────────────────────────────

    public getTotal(): number    { return this.validationResponse?.itens?.length ?? 0; }
    public getNovos(): number    { return this.validationResponse?.resumo?.totalNovos ?? 0; }
    public getAvisos(): number   { return this.validationResponse?.resumo?.totalAvisos ?? 0; }
    public getErros(): number    { return this.validationResponse?.resumo?.totalErros ?? 0; }

    public get isImportDisabled(): boolean {
        if (!this.validationResponse) return true;
        if (this.getErros() > 0) return true;
        if (this.getNovos() === 0) return true;
        return false;
    }

    public getButtonTooltip(): string {
        if (this.getErros() > 0) return 'Corrija os erros e importe a planilha novamente';
        return '';
    }

    public getTooltip(item: ItemValidacaoMovimentacaoStruct): string {
        const parts: string[] = [];
        if (item.erros?.length)  parts.push(`Erros: ${item.erros.join(', ')}`);
        if (item.avisos?.length) parts.push(`Avisos: ${item.avisos.join(', ')}`);
        return parts.join('\n') || (item.isValido ? 'Lançamento válido' : '');
    }

    public getRowClass(item: ItemValidacaoMovimentacaoStruct): string {
        if (!item.isValido) return 'row-error';
        if (item.isAlerta)  return 'row-warning';
        return '';
    }
}
